import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import { mainSiteUrl } from '@/mocks/categories';
import JsonLd, { siteUrl, siteName, logoUrl } from '@/components/feature/JsonLd';
import { articles, getArticleBySlug, type Article, type ArticleLocale } from '@/data/articles';

function pickLocale(article: Article, lang: string): ArticleLocale {
  if (lang === 'pl') return article.pl;
  if (lang === 'es') return article.es;
  if (lang === 'fr') return article.fr;
  return article.en;
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article = slug ? getArticleBySlug(slug) : undefined;
  const locale = article ? pickLocale(article, i18n.language) : undefined;

  useEffect(() => {
    if (locale) {
      document.title = locale.title;
    }
  }, [locale]);

  if (!article || !locale) {
    return (
      <div className="min-h-screen bg-[#fbfaf8]">
        <SiteHeader />
        <main className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
          <h1 className="text-2xl font-bold text-stone-900 md:text-3xl">Guide not found</h1>
          <p className="mt-3 text-stone-600">This guide does not exist or has been moved.</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
            {t('backHome')}
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: locale.title,
        description: locale.description,
        inLanguage: i18n.language,
        dateModified: '2026-08-23',
        url: `${siteUrl}/article/${article.slug}`,
        publisher: {
          '@type': 'Organization',
          name: siteName,
          logo: { '@type': 'ImageObject', url: logoUrl },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteUrl}/article/${article.slug}`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: locale.title, item: `${siteUrl}/article/${article.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#fbfaf8]">
      <JsonLd data={articleJsonLd} />
      <SiteHeader />
      <main>
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          <nav className="flex items-center gap-2 text-xs text-stone-500">
            <Link to="/" className="flex items-center gap-1 hover:text-stone-800">
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              {t('breadcrumbHome')}
            </Link>
            <span className="text-stone-300">/</span>
            <span className="text-stone-700">{locale.tag}</span>
          </nav>

          <header className="mt-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                {locale.tag}
              </span>
              <span className="text-xs text-stone-400">{t('updated')}</span>
            </div>
            <h1 className="mt-4 text-2xl font-bold leading-tight text-stone-900 md:text-3xl lg:text-4xl">
              {locale.title}
            </h1>
          </header>

          <div className="mt-6 space-y-5 text-base leading-relaxed text-stone-700">
            {locale.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>

          {locale.sections.map((section) => (
            <section key={section.title} className="mt-10">
              <h2 className="text-xl font-bold text-stone-900 md:text-2xl">{section.title}</h2>
              <div className="mt-4 space-y-4 text-base leading-relaxed text-stone-700">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          ))}

          <section className="mt-10">
            <h2 className="text-xl font-bold text-stone-900 md:text-2xl">{t('faqTitle')}</h2>
            <div className="mt-4 space-y-2">
              {locale.faq.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div key={item.q} className="overflow-hidden rounded-lg border border-stone-200 bg-white">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between px-4 py-3.5 text-left text-sm font-semibold text-stone-800 hover:bg-stone-50 cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span>{item.q}</span>
                      <i
                        className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-stone-400 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                    {isOpen && (
                      <div className="border-t border-stone-100 px-4 py-3 text-sm leading-relaxed text-stone-600">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="mt-12 rounded-lg border border-stone-200 bg-white p-6 text-center md:p-8">
            <h2 className="text-lg font-bold text-stone-900 md:text-xl">{t('ctaTitle')}</h2>
            <p className="mt-2 text-sm text-stone-600">{t('ctaSubtitle')}</p>
            <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 whitespace-nowrap cursor-pointer"
              >
                {t('ctaButton')}
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-stone-200 px-6 py-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
                {t('backHome')}
              </Link>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-lg font-bold text-stone-900 md:text-xl">{t('relatedTitle')}</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {related.map((relatedArticle) => {
                const relatedLocale = pickLocale(relatedArticle, i18n.language);
                return (
                  <Link
                    key={relatedArticle.slug}
                    to={`/article/${relatedArticle.slug}`}
                    className="group flex flex-col rounded-lg border border-stone-200 bg-white p-4 hover:border-emerald-300 cursor-pointer"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                      {relatedLocale.tag}
                    </span>
                    <span className="mt-2 text-sm font-semibold leading-snug text-stone-800 group-hover:text-emerald-700">
                      {relatedLocale.title}
                    </span>
                    <span className="mt-2 flex items-center gap-1 text-xs font-medium text-stone-500">
                      {t('articles.readMore')}
                      <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}